import { Action } from 'redux';
import { ContractAddendum } from '../../../models/Entities/Contracts/ContractAddendum';
import { ContractNegotiation } from '../../../models/Entities/Contracts/ContractNegotiation';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { ContractState, ContractUnloadState } from '../../states/ContractState';

const UpdateContractInList = (currentState: ContractState, updatedContract?: Contract): Contract[] => {
  if (!updatedContract) {
    return currentState.list || [];
  }

  return (currentState.list || []).map((item: Contract) => item.id === updatedContract.id ? updatedContract : item);
};

const UpsertNegotiation = (negotiations: ContractNegotiation[] | undefined, contractNegotiation: ContractNegotiation): ContractNegotiation[] => {
  const currentNegotiations = negotiations || [];
  const exists = currentNegotiations.some((item: ContractNegotiation) => item.id === contractNegotiation.id);

  if (!exists) {
    return [...currentNegotiations, contractNegotiation];
  }

  return currentNegotiations.map((item: ContractNegotiation) => item.id === contractNegotiation.id ? contractNegotiation : item);
};

const UpdateNegotiationStatus = (
  negotiations: ContractNegotiation[] | undefined,
  contractNegotiation: ContractNegotiation,
  partial: Partial<ContractNegotiation>
): ContractNegotiation[] => (negotiations || []).map((item: ContractNegotiation) => {
  if (item.id !== contractNegotiation.id) {
    return item;
  }

  return {
    ...item,
    ...partial,
  } as ContractNegotiation;
});

const UpdateAddendum = (
  addendum: ContractAddendum,
  addendumId: number,
  updater: (currentAddendum: ContractAddendum) => ContractAddendum
): ContractAddendum => addendum.id === addendumId ? updater(addendum) : addendum;

const UpdateContractAddendums = (
  contract: Contract,
  addendumId: number,
  updater: (currentAddendum: ContractAddendum) => ContractAddendum
): ContractAddendum[] => (contract.addendums || []).map((addendum: ContractAddendum) => UpdateAddendum(addendum, addendumId, updater));

const FindContract = (currentState: ContractState, contractId: number): Contract | undefined =>
  currentState.list?.find((item: Contract) => item.id === contractId);

const BuildState = (currentState: ContractState, updatedContract?: Contract): ContractState => ({
  ...currentState,
  list: UpdateContractInList(currentState, updatedContract),
});

const HandleContractFlags = (
  currentState: ContractState,
  contractId: number,
  partial: Partial<Contract>
): ContractState => {
  const currentContract = FindContract(currentState, contractId);
  if (!currentContract) {
    return currentState;
  }

  return BuildState(currentState, {
    ...currentContract,
    ...partial,
  });
};

const HandleAddendumFlags = (
  currentState: ContractState,
  contractId: number,
  addendumId: number,
  updater: (currentAddendum: ContractAddendum) => ContractAddendum
): ContractState => {
  const currentContract = FindContract(currentState, contractId);
  if (!currentContract) {
    return currentState;
  }

  return BuildState(currentState, {
    ...currentContract,
    addendums: UpdateContractAddendums(currentContract, addendumId, updater),
  });
};

const HandleNegotiationUpsert = (currentState: ContractState, contractNegotiation: ContractNegotiation): ContractState => {
  const currentContract = FindContract(currentState, contractNegotiation.contractId);
  if (!currentContract) {
    return currentState;
  }

  return BuildState(currentState, {
    ...currentContract,
    negotiations: UpsertNegotiation(currentContract.negotiations, contractNegotiation),
    addendums: UpdateContractAddendums(currentContract, contractNegotiation.contractAddendumId, (addendum: ContractAddendum) => ({
      ...addendum,
      negotiations: UpsertNegotiation(addendum.negotiations, contractNegotiation),
    } as ContractAddendum)),
  });
};

const HandleNegotiationStatus = (
  currentState: ContractState,
  contractNegotiation: ContractNegotiation,
  partial: Partial<ContractNegotiation>
): ContractState => {
  const currentContract = FindContract(currentState, contractNegotiation.contractId);
  if (!currentContract) {
    return currentState;
  }

  return BuildState(currentState, {
    ...currentContract,
    negotiations: UpdateNegotiationStatus(currentContract.negotiations, contractNegotiation, partial),
    addendums: UpdateContractAddendums(currentContract, contractNegotiation.contractAddendumId, (addendum: ContractAddendum) => ({
      ...addendum,
      negotiations: UpdateNegotiationStatus(addendum.negotiations, contractNegotiation, partial),
    } as ContractAddendum)),
  });
};

const HandleNegotiationDelete = (
  currentState: ContractState,
  contractId: number,
  addendumId: number,
  negotiationId: string
): ContractState => {
  const currentContract = FindContract(currentState, contractId);
  if (!currentContract) {
    return currentState;
  }

  return BuildState(currentState, {
    ...currentContract,
    negotiations: (currentContract.negotiations || []).filter((item: ContractNegotiation) => item.id !== negotiationId),
    addendums: UpdateContractAddendums(currentContract, addendumId, (addendum: ContractAddendum) => ({
      ...addendum,
      negotiations: (addendum.negotiations || []).filter((item: ContractNegotiation) => item.id !== negotiationId),
    } as ContractAddendum)),
  });
};

const Handlers: Record<string, (currentState: ContractState, action: any) => ContractState> = {
  CONTRACTNEGOTIATION_GET_ALL_BY_CONTRACTID_REQUEST_ACTION: (currentState, action) =>
    HandleContractFlags(currentState, action.contractId, {
      isGettingContractNegotiations: true,
      isGettingContractNegotiationsSuccess: false,
      FailGettingContractNegotiations: false,
      ErrorGettingContractNegotiations: undefined,
    }),
  CONTRACTNEGOTIATION_GET_ALL_BY_CONTRACTID_RECEIVE_ACTION: (currentState, action) =>
    HandleContractFlags(currentState, action.contractId, {
      negotiations: action.contractNegotiations,
      isGettingContractNegotiations: false,
      isGettingContractNegotiationsSuccess: true,
      FailGettingContractNegotiations: false,
      ErrorGettingContractNegotiations: undefined,
    }),
  CONTRACTNEGOTIATION_GET_ALL_BY_CONTRACTID_ERROR_ACTION: (currentState, action) =>
    HandleContractFlags(currentState, action.contractId, {
      isGettingContractNegotiations: false,
      isGettingContractNegotiationsSuccess: false,
      FailGettingContractNegotiations: true,
      ErrorGettingContractNegotiations: action.error,
    }),
  CONTRACTNEGOTIATION_GET_ALL_BY_ADDENDUMID_REQUEST_ACTION: (currentState, action) =>
    HandleAddendumFlags(currentState, action.contractId, action.addendumId, (addendum: ContractAddendum) => ({
      ...addendum,
      isGettingContractNegotiations: true,
      isGettingContractNegotiationsSuccess: false,
      FailGettingContractNegotiations: false,
      ErrorGettingContractNegotiations: undefined,
    } as ContractAddendum)),
  CONTRACTNEGOTIATION_GET_ALL_BY_ADDENDUMID_RECEIVE_ACTION: (currentState, action) =>
    HandleAddendumFlags(currentState, action.contractId, action.addendumId, (addendum: ContractAddendum) => ({
      ...addendum,
      negotiations: action.contractNegotiations,
      isGettingContractNegotiations: false,
      isGettingContractNegotiationsSuccess: true,
      FailGettingContractNegotiations: false,
      ErrorGettingContractNegotiations: undefined,
    } as ContractAddendum)),
  CONTRACTNEGOTIATION_GET_ALL_BY_ADDENDUMID_ERROR_ACTION: (currentState, action) =>
    HandleAddendumFlags(currentState, action.contractId, action.addendumId, (addendum: ContractAddendum) => ({
      ...addendum,
      isGettingContractNegotiations: false,
      isGettingContractNegotiationsSuccess: false,
      FailGettingContractNegotiations: true,
      ErrorGettingContractNegotiations: action.error,
    } as ContractAddendum)),
  CONTRACTNEGOTIATION_GET_BY_ID_RECEIVE_ACTION: (currentState, action) => HandleNegotiationUpsert(currentState, action.contractNegotiation),
  CONTRACTNEGOTIATION_CREATE_RECEIVE_ACTION: (currentState, action) => HandleNegotiationUpsert(currentState, action.contractNegotiation),
  CONTRACTNEGOTIATION_UPDATE_RECEIVE_ACTION: (currentState, action) => HandleNegotiationUpsert(currentState, action.contractNegotiation),
  CONTRACTNEGOTIATION_UPDATE_REQUEST_ACTION: (currentState, action) => HandleNegotiationStatus(currentState, action.contractNegotiation, {
    isUpdating: true,
    isUpdatedSuccessfully: undefined,
    failOnUpdating: false,
    error: undefined,
  }),
  CONTRACTNEGOTIATION_UPDATE_ERROR_ACTION: (currentState, action) => HandleNegotiationStatus(currentState, action.contractNegotiation, {
    isUpdating: false,
    isUpdatedSuccessfully: false,
    failOnUpdating: true,
    error: action.error,
  }),
  CONTRACTNEGOTIATION_DELETE_RECEIVE_ACTION: (currentState, action) =>
    HandleNegotiationDelete(currentState, action.contractId, action.contractAddendumId, action.id),
};

export default function ContractNegotiationsReducer(
  state: ContractState | undefined,
  action: Action
): ContractState {
  const currentState = state ?? ContractUnloadState;
  const a: any = action;
  const handler = Handlers[a.type];

  if (!handler) {
    return currentState;
  }

  return handler(currentState, a);
}